/**
 * Chart Data Projection Helper
 * 
 * Projects missing values in chart data (ECharts format) using FV-based interpolation.
 * Works with bar charts, line charts, and other series-based charts.
 */

import { EChartsOption } from 'echarts';
import { 
  extractYearFromColumn, 
  isYearColumn 
} from './TableDataProjectionHelper';

/**
 * ECharts series data point - can be number or null
 */
export type SeriesDataPoint = number | null;

/**
 * ECharts series configuration
 */
export type ChartSeries = {
  name: string;
  type: string;
  data: SeriesDataPoint[];
  [key: string]: any; // Allow other ECharts properties
};

/**
 * Non-projectable chart types (relationship-based, not time-series)
 */
const NON_PROJECTABLE_CHART_TYPES = ['graph', 'sankey', 'tree', 'treemap', 'sunburst', 'scatter'];

const DIVERGENCE_THRESHOLD = 5;

/**
 * Check if a chart is projectable (time-series based)
 * Returns false for relationship-based charts (graph, network, tree, etc.)
 */
const isChartProjectable = (chartConfig: EChartsOption): boolean => {
  const series = (chartConfig.series || []) as ChartSeries[];
  
  if (series.length === 0) {
    return false;
  }
  
  // Check if any series is a non-projectable (relationship-based) type.
  // Note: stacked bar/line series (e.g. "stacked total" market-size charts)
  // are still projectable — only the chart *types* above are excluded.
  const hasNonProjectableType = series.some((s: ChartSeries) =>
    NON_PROJECTABLE_CHART_TYPES.includes(s.type?.toLowerCase())
  );
  
  if (hasNonProjectableType) {
    return false;
  }
  
  return true;
};

/**
 * Extract year from series name
 * Examples: "2024 Market Size ($B)" → 2024, "2034/35 Forecast ($B)" → 2034
 */
const extractYearFromSeriesName = (seriesName: string): number | null => {
  return extractYearFromColumn(seriesName);
};

/**
 * Calculate CAGR from two data points
 * Formula: CAGR = (FV/PV)^(1/nper) - 1
 */
const calculateCAGRFromPoints = (
  pv: number,
  fv: number,
  yearDiff: number
): number | null => {
  if (yearDiff <= 0 || pv <= 0 || fv <= 0) {
    return null;
  }
  
  const cagr = Math.pow(fv / pv, 1 / yearDiff) - 1;
  return cagr;
};

/**
 * Calculate future value using FV formula
 * FV = PV * (1 + rate)^nper
 * Result is rounded to 2 decimal places
 */
const calculateFutureValue = (pv: number, rate: number, nper: number): number => {
  if (rate === 0) {
    return pv;
  }
  const fv = pv * Math.pow(1 + rate, nper);
  return Math.round(fv * 100) / 100;
};

/**
 * Find CAGR for a specific category (company/provider) across series
 * Returns the calculated CAGR based on available data points
 */
const findCAGRForCategory = (
  categoryIndex: number,
  seriesWithYears: Array<{ series: ChartSeries; year: number; index: number }>
): number | null => {
  // Collect all known values for this category
  const knownPoints: Array<{ year: number; value: number }> = [];
  
  seriesWithYears.forEach(({ series, year }) => {
    const value = series.data[categoryIndex];
    if (value !== null && value !== undefined && typeof value === 'number' && value > 0) {
      knownPoints.push({ year, value });
    }
  });
  
  // Need at least 2 points to calculate CAGR
  if (knownPoints.length < 2) {
    return null;
  }
  
  // Sort by year and use first and last for largest time span
  knownPoints.sort((a, b) => a.year - b.year);
  const firstPoint = knownPoints[0];
  const lastPoint = knownPoints[knownPoints.length - 1];
  
  const yearDiff = lastPoint.year - firstPoint.year;
  return calculateCAGRFromPoints(firstPoint.value, lastPoint.value, yearDiff);
};

/**
 * Result of chart data projection
 */
export type ProjectedChartData = {
  data: EChartsOption;
  isFilled: boolean;
};

/**
 * Add formatters to mark projected values with '*' in labels and tooltips
 */
const addProjectedValueFormatters = (
  config: EChartsOption,
  series: ChartSeries[],
  projectedIndices: Array<{ seriesIndex: number; categoryIndex: number }>
): void => {
  // Add label formatter for chart labels
  series.forEach((s: ChartSeries, seriesIndex: number) => {
    if (!s.label) s.label = {};
    
    (s.label as any).formatter = (params: any) => {
      const isProjected = projectedIndices.some(
        (item) => item.seriesIndex === seriesIndex && item.categoryIndex === params.dataIndex
      );
      return isProjected ? `${params.value}*` : params.value;
    };
  });
  
  // Add tooltip formatter
  if (!config.tooltip) (config as any).tooltip = {};
  
  (config.tooltip as any).formatter = (params: any) => {
    const items = Array.isArray(params) ? params : [params];
    let result = '';
    
    items.forEach((item: any, idx: number) => {
      if (item.value === null || item.value === undefined) {
        return;
      }
      
      const isProjected = projectedIndices.some(
        (p) => p.seriesIndex === item.seriesIndex && p.categoryIndex === item.dataIndex
      );
      
      const valueClass = isProjected ? 'chart-projected-value' : 'chart-original-value';
      const displayValue = `<span class="${valueClass}">${item.value}${isProjected ? '*' : ''}</span>`;
      
      if (idx === 0 && item.name) result += `${item.name}<br/>`;
      result += `${item.marker || ''} ${item.seriesName || ''}: ${displayValue}<br/>`;
    });
    
    return result;
  };
};

/**
 * Check if xAxis contains year data
 * Handles both xAxis as object and xAxis as array
 */
const hasYearsInXAxis = (xAxis: any): boolean => {
  if (!xAxis) return false;
  
  // Handle xAxis as array (ECharts allows array for multiple axes)
  let xAxisData: any[] | undefined;
  if (Array.isArray(xAxis)) {
    // Get data from first axis
    xAxisData = xAxis[0]?.data;
  } else {
    // xAxis is an object
    xAxisData = xAxis.data;
  }
  
  if (!xAxisData || !Array.isArray(xAxisData)) return false;
  
  // Check if at least 2 items in xAxis look like years
  const yearCount = xAxisData.filter((item: any) => {
    const year = extractYearFromColumn(String(item));
    return year !== null;
  }).length;
  
  return yearCount >= 2;
};

/**
 * Project data when years are in xAxis (transposed structure)
 * Each series represents a company/category, xAxis has years
 */
const projectTransposedChartData = (
  config: EChartsOption,
  markProjected: boolean
): ProjectedChartData => {
  const newConfig = JSON.parse(JSON.stringify(config)) as EChartsOption;
  const series = (newConfig.series || []) as ChartSeries[];
  
  // Handle xAxis as both array and object
  let xAxisData: string[];
  const xAxis = newConfig.xAxis;
  if (Array.isArray(xAxis)) {
    xAxisData = (xAxis[0]?.data || []) as string[];
  } else {
    xAxisData = ((xAxis as any)?.data || []) as string[];
  }
  
  // Extract years from xAxis
  const yearsWithIndices = xAxisData
    .map((label: string, index: number) => {
      const year = extractYearFromColumn(label);
      return year ? { year, index } : null;
    })
    .filter((item): item is { year: number; index: number } => item !== null);
  
  if (yearsWithIndices.length < 2) {
    return { data: newConfig, isFilled: false };
  }
  
  const projectedIndices: Array<{ seriesIndex: number; categoryIndex: number }> = [];
  let hasAnyFilledValue = false;
  
  // Process each series (company/category)
  series.forEach((s: ChartSeries, seriesIndex: number) => {
    // Find known data points for this series
    const knownPoints: Array<{ year: number; value: number; index: number }> = [];
    
    yearsWithIndices.forEach(({ year, index }) => {
      const value = s.data[index];
      if (value !== null && value !== undefined && typeof value === 'number' && value > 0) {
        knownPoints.push({ year, value, index });
      }
    });
    
    if (knownPoints.length < 2) return; // Need at least 2 points
    
    // Calculate CAGR from first and last known points
    knownPoints.sort((a, b) => a.year - b.year);
    const firstPoint = knownPoints[0];
    const lastPoint = knownPoints[knownPoints.length - 1];
    const yearDiff = lastPoint.year - firstPoint.year;
    const cagr = calculateCAGRFromPoints(firstPoint.value, lastPoint.value, yearDiff);
    
    if (!cagr) return;
    
    // Project missing values
    yearsWithIndices.forEach(({ year, index }) => {
      if (s.data[index] === null || s.data[index] === undefined) {
        const yearDiffFromBase = year - firstPoint.year;
        const projectedValue = calculateFutureValue(firstPoint.value, cagr, yearDiffFromBase);
        
        s.data[index] = projectedValue;
        hasAnyFilledValue = true;
        
        if (markProjected) {
          projectedIndices.push({ seriesIndex, categoryIndex: index });
        }
      }
    });
  });
  
  if (markProjected && projectedIndices.length > 0) {
    (newConfig as any)._projectedIndices = projectedIndices;
    addProjectedValueFormatters(newConfig, series, projectedIndices);
  }
  
  return { data: newConfig, isFilled: hasAnyFilledValue };
};

/**
 * Project missing values in chart series data
 * 
 * This function:
 * 1. Checks if chart is projectable (skips relationship-based charts like graph, tree, etc.)
 * 2. Detects chart structure (years in series names OR years in xAxis)
 * 3. For each category/series, calculates CAGR from available data
 * 4. Projects missing values using FV formula
 * 5. Returns new chart config with projected values and isFilled flag
 * 
 * @param chartConfig - Original ECharts configuration
 * @param markProjected - If true, adds metadata to track projected values
 * @returns Object with { data: ChartConfig, isFilled: boolean }
 */
export const projectChartData = (
  chartConfig: EChartsOption,
  markProjected: boolean = false
): ProjectedChartData => {
  // Skip projection for non-time-series charts (graph, tree, sankey, etc.)
  if (!isChartProjectable(chartConfig)) {
    return { data: chartConfig, isFilled: false };
  }
  
  // Check if this is a transposed structure (years in xAxis)
  if (hasYearsInXAxis(chartConfig.xAxis)) {
    return projectTransposedChartData(chartConfig, markProjected);
  }
  
  // Original logic: years in series names
  const newConfig = JSON.parse(JSON.stringify(chartConfig)) as EChartsOption;
  
  const series = (newConfig.series || []) as ChartSeries[];
  const seriesWithYears = series
    .map((s: ChartSeries, index: number) => {
      const year = extractYearFromSeriesName(s.name);
      return year ? { series: s, year, index } : null;
    })
    .filter((item): item is { series: ChartSeries; year: number; index: number } => item !== null);
  
  if (seriesWithYears.length < 2) {
    return { data: newConfig, isFilled: false };
  }
  
  seriesWithYears.sort((a, b) => a.year - b.year);
  const categoryCount = seriesWithYears[0].series.data.length;
  
  const projectedIndices: Array<{ seriesIndex: number; categoryIndex: number }> = [];
  let hasAnyFilledValue = false;
  
  for (let categoryIndex = 0; categoryIndex < categoryCount; categoryIndex++) {
    // Calculate CAGR for this category
    const cagr = findCAGRForCategory(categoryIndex, seriesWithYears);
    
    if (!cagr) {
      continue; // Cannot calculate projections without CAGR
    }
    
    // Find a base value (first non-null value)
    let baseValue: number | null = null;
    let baseYear: number | null = null;
    
    for (const { series, year } of seriesWithYears) {
      const value = series.data[categoryIndex];
      if (value !== null && value !== undefined && typeof value === 'number') {
        baseValue = value;
        baseYear = year;
        break;
      }
    }
    
    if (baseValue === null || baseYear === null) {
      continue; // No base value found
    }
    
    // Project missing values
    seriesWithYears.forEach(({ series: s, year, index }) => {
      const currentValue = s.data[categoryIndex];
      
      if (currentValue === null || currentValue === undefined) {
        const yearDiff = year - baseYear!;
        const projectedValue = calculateFutureValue(baseValue!, cagr, yearDiff);
        
        // Update the series data (cast to access by index)
        series[index].data[categoryIndex] = projectedValue;
        hasAnyFilledValue = true;
        
        if (markProjected) {
          projectedIndices.push({ seriesIndex: index, categoryIndex });
        }
      }
    });
  }
  
  // Mark projected values if requested
  if (markProjected && projectedIndices.length > 0) {
    (newConfig as any)._projectedIndices = projectedIndices;
    addProjectedValueFormatters(newConfig, series, projectedIndices);
  }
  
  return {
    data: newConfig,
    isFilled: hasAnyFilledValue
  };
};

/**
 * Check if a specific data point was projected
 */
export const isProjectedValue = (
  chartConfig: EChartsOption,
  seriesIndex: number,
  categoryIndex: number
): boolean => {
  const projectedIndices = (chartConfig as any)._projectedIndices;
  
  if (!projectedIndices || !Array.isArray(projectedIndices)) {
    return false;
  }
  
  return projectedIndices.some(
    (item: any) => item.seriesIndex === seriesIndex && item.categoryIndex === categoryIndex
  );
};

/**
 * Get projection statistics for the chart
 */
export const getProjectionStats = (chartConfig: EChartsOption): {
  totalDataPoints: number;
  originalValues: number;
  projectedValues: number;
  nullValues: number;
} => {
  let totalDataPoints = 0;
  let originalValues = 0;
  let nullValues = 0;
  
  // Cast series to ChartSeries[] to work around ECharts' complex union types
  const series = (chartConfig.series || []) as ChartSeries[];
  
  series.forEach((s: ChartSeries) => {
    s.data.forEach((value: number | null) => {
      totalDataPoints++;
      if (value === null || value === undefined) {
        nullValues++;
      } else {
        originalValues++;
      }
    });
  });
  
  const projectedIndices = (chartConfig as any)._projectedIndices || [];
  const projectedValues = projectedIndices.length;
  
  return {
    totalDataPoints,
    originalValues: originalValues - projectedValues,
    projectedValues,
    nullValues: nullValues - projectedValues
  };
};

export {
  isChartProjectable
};

/**
 * Detects when one series is disproportionately larger than others at the same x-axis points.
 * Returns an HTML warning string for the chart footer, or null if no divergence is found.
 * Uses median-based ratio to avoid being skewed by outliers in the comparison set itself.
 */
export const detectSeriesDivergence = (chartConfig: EChartsOption): string | null => {
  if (!isChartProjectable(chartConfig)) return null;

  const allSeries = (chartConfig.series || []) as ChartSeries[];
  if (allSeries.length < 2) return null;

  // When chart has mixed scopes, return a fixed explanation note for the dual-axis layout.
  const broaderNames = allSeries.filter((s: any) => s.scopeType === 'broader').map(s => s.name);
  if (broaderNames.length > 0 && allSeries.some((s: any) => s.scopeType !== 'broader')) {
    return `<b>Scope Note:</b> Dashed lines (right axis) represent the aggregate/total market. Solid lines (left axis) represent specific sub-segments. Different scales are used for each axis.`;
  }

  const series = allSeries;
  if (series.length < 2) return null;

  const dataLength = Math.max(...series.map(s => s.data?.length ?? 0));
  if (dataLength === 0) return null;

  let outlierName: string | null = null;
  let peakRatio = 0;

  for (let i = 0; i < dataLength; i++) {
    const points: Array<{ value: number; name: string }> = [];
    series.forEach(s => {
      const v = s.data?.[i];
      if (typeof v === 'number' && v > 0) points.push({ value: v, name: s.name });
    });

    if (points.length < 2) continue;

    points.sort((a, b) => a.value - b.value);

    const top = points[points.length - 1];

    // 2-series: compare max to min; 3+: compare max to median-of-all
    const mid = Math.floor(points.length / 2);
    const referenceValue = points.length === 2
      ? points[0].value
      : points.length % 2 !== 0
        ? points[mid].value
        : (points[mid - 1].value + points[mid].value) / 2;

    if (referenceValue <= 0) continue;

    const ratio = top.value / referenceValue;

    if (ratio >= DIVERGENCE_THRESHOLD && ratio > peakRatio) {
      peakRatio = ratio;
      outlierName = top.name;
    }
  }

  if (!outlierName) return null;

  const reason = /overall|total|global/i.test(outlierName)
    ? 'a broader market scope definition'
    : 'different methodology or geographic coverage';

  return `<b>Methodology Note:</b> "${outlierName}" is ~${Math.round(peakRatio)}× larger than other sources — likely ${reason}. Verify source definitions before comparing.`;
};

export const applyDualAxis = (chartConfig: EChartsOption): EChartsOption => {
  const series = (chartConfig.series || []) as ChartSeries[];
  const broaderSeries = series.filter((s: any) => s.scopeType === 'broader');

  if (broaderSeries.length === 0) return chartConfig;

  const config = JSON.parse(JSON.stringify(chartConfig)) as EChartsOption;
  const seriesCopy = (config.series || []) as ChartSeries[];
  const broaderNames = new Set(broaderSeries.map(s => s.name));

  const existingYAxis: any = Array.isArray(config.yAxis)
    ? config.yAxis[0]
    : config.yAxis ?? { type: 'value' };

  config.yAxis = [
    { ...existingYAxis, name: 'Market Segment ($B)', position: 'left' },
    {
      type: 'value',
      name: 'Total Market ($B)',
      position: 'right',
      nameLocation: 'middle',
      nameGap: 60,
      splitLine: { show: false },
      axisLabel: { formatter: '{value}B' },
    },
  ];

  (config as any).series = seriesCopy.map((s: any) =>
    broaderNames.has(s.name)
      ? { ...s, yAxisIndex: 1, lineStyle: { ...(s.lineStyle || {}), type: 'dashed', opacity: 0.7 } }
      : { ...s, yAxisIndex: 0 }
  );

  (config as any).grid = { ...((config as any).grid || {}), right: 90 };

  // Move xAxis name to center so it doesn't overlap the right axis labels
  const xAxis = config.xAxis;
  if (Array.isArray(xAxis)) {
    (config as any).xAxis = xAxis.map((ax: any, i: number) =>
      i === 0 ? { ...ax, nameLocation: 'middle', nameGap: 25 } : ax
    );
  } else if (xAxis) {
    (config as any).xAxis = { ...(xAxis as any), nameLocation: 'middle', nameGap: 25 };
  }

  return config;
};

export default {
  isChartProjectable,
  projectChartData,
  isProjectedValue,
  getProjectionStats,
  detectSeriesDivergence,
  applyDualAxis,
};
